/**
 * Landing V2 sections that sell the moat (docs/landing/PLAN_LANDING_V2.md): closed agent loop, control tower,
 * Studio, compact pricing and the compare strip. Same primitives and `.mk-*` tokens as the other section files;
 * diagrams are CSS/Tailwind (no screenshots that go stale), screenshots only where the real page is the argument.
 */
import { useState } from 'react'
import type { ReactNode } from 'react'
import { usePricing } from '#/lib/api'
import { UNLIMITED, resolveParallelLimits } from '#/lib/runner/parallel'
import { getLocale } from '#/paraglide/runtime'
import { Link } from '@tanstack/react-router'
import {
  ArrowRight,
  BadgeCheck,
  Bot,
  Calculator,
  Check,
  FileSignature,
  FolderArchive,
  Layers,
  Lock,
  Package,
  PlayCircle,
  RotateCcw,
  ShieldCheck,
  Users,
  Waves,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'
import {
  Container,
  FeatureCol,
  Frame,
  MkLink,
  Overline,
  SectionHead,
} from './mk'

// ---------------------------------------------------------------- 5. closed agent loop (dark)

type LoopStep = {
  cmd: string
  title: string
  desc: string
  gate?: boolean
  last?: boolean
}

function LoopCard({ step, i }: { step: LoopStep; i: number }) {
  const Arrow = step.last ? RotateCcw : ArrowRight
  return (
    <li
      className={cn(
        'relative flex flex-col gap-3 rounded-xl border bg-[var(--mk-dark-card)] p-5',
        step.gate
          ? 'border-[#7d9bff]/70 shadow-[0_0_0_1px_rgba(125,155,255,.25),0_20px_50px_-30px_rgba(125,155,255,.6)]'
          : 'border-[var(--mk-dark-border)]',
      )}
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs text-[var(--mk-dark-fg-muted)]">
          0{i + 1}
        </span>
        <Arrow
          className={cn(
            'size-4',
            step.last ? 'text-[#7d9bff]' : 'text-[var(--mk-dark-fg-muted)]',
          )}
          aria-hidden
        />
      </div>
      <code
        className={cn(
          'w-fit rounded-md px-2 py-1 font-mono text-[12px] leading-none',
          step.gate
            ? 'bg-[#7d9bff]/15 text-[#b8c8ff]'
            : 'bg-white/5 text-[var(--mk-dark-fg)]',
        )}
      >
        {step.cmd}
      </code>
      <h3 className="text-base! leading-snug!">{step.title}</h3>
      <p className="text-sm leading-relaxed text-[var(--mk-dark-fg-muted)]">
        {step.desc}
      </p>
    </li>
  )
}

function DarkCard({
  icon: Icon,
  title,
  children,
  className,
}: {
  icon: LucideIcon
  title: string
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-xl border border-[var(--mk-dark-border)] bg-[var(--mk-dark-card)] p-6',
        className,
      )}
    >
      <div className="flex size-9 items-center justify-center rounded-lg border border-[var(--mk-dark-border)] bg-white/5 text-[var(--mk-dark-fg)]">
        <Icon className="size-4" aria-hidden />
      </div>
      <h3 className="text-lg!">{title}</h3>
      <div className="text-sm leading-relaxed text-[var(--mk-dark-fg-muted)]">
        {children}
      </div>
    </div>
  )
}

export function LoopSection() {
  const steps: LoopStep[] = [
    {
      cmd: m.mk_loop_1_cmd(),
      title: m.mk_loop_1_title(),
      desc: m.mk_loop_1_desc(),
    },
    {
      cmd: m.mk_loop_2_cmd(),
      title: m.mk_loop_2_title(),
      desc: m.mk_loop_2_desc(),
    },
    {
      cmd: m.mk_loop_3_cmd(),
      title: m.mk_loop_3_title(),
      desc: m.mk_loop_3_desc(),
    },
    {
      cmd: m.mk_loop_4_cmd(),
      title: m.mk_loop_4_title(),
      desc: m.mk_loop_4_desc(),
    },
    {
      cmd: m.mk_loop_5_cmd(),
      title: m.mk_loop_5_title(),
      desc: m.mk_loop_5_desc(),
      gate: true,
    },
    {
      cmd: m.mk_loop_6_cmd(),
      title: m.mk_loop_6_title(),
      desc: m.mk_loop_6_desc(),
      last: true,
    },
  ]
  const modes: Array<{ icon: LucideIcon; title: string; desc: string }> = [
    {
      icon: PlayCircle,
      title: m.mk_loop_mode_1_title(),
      desc: m.mk_loop_mode_1_desc(),
    },
    {
      icon: BadgeCheck,
      title: m.mk_loop_mode_2_title(),
      desc: m.mk_loop_mode_2_desc(),
    },
    {
      icon: Lock,
      title: m.mk_loop_mode_3_title(),
      desc: m.mk_loop_mode_3_desc(),
    },
  ]
  return (
    <section id="loop" className="mk-dark-section scroll-mt-20 py-24 lg:py-32">
      <Container>
        <SectionHead
          align="left"
          overline={
            <span className="text-[#7d9bff]">{m.mk_loop_overline()}</span>
          }
          title={m.mk_loop_title()}
          lead={m.mk_loop_sub()}
          actions={
            <>
              <MkLink
                to="/sign-in"
                search={{ redirect: '/home' }}
                className="bg-white! text-[var(--mk-btn)]! hover:bg-[#e8eaee]!"
              >
                {m.mk_loop_cta_primary()}
              </MkLink>
              <MkLink
                to="/docs"
                variant="secondary"
                arrow
                className="border-[var(--mk-dark-border)]! text-white! hover:bg-white/5!"
              >
                {m.mk_loop_cta_secondary()}
              </MkLink>
            </>
          }
        />
        <div id="agent" className="grid gap-7 pt-10 md:grid-cols-3">
          <FeatureCol title={m.mk_agent_1_title()}>
            {m.mk_agent_1_desc()}
          </FeatureCol>
          <FeatureCol title={m.mk_agent_2_title()}>
            {m.mk_agent_2_desc()}
          </FeatureCol>
          <FeatureCol title={m.mk_agent_3_title()}>
            {m.mk_agent_3_desc()}
          </FeatureCol>
        </div>
        <details className="mk-loop-details mt-8">
          <summary>{m.mk_loop_details()}</summary>
          <ol className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {steps.map((s, i) => (
              <LoopCard key={s.cmd} step={s} i={i} />
            ))}
          </ol>

          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-3">
              <Overline className="text-[#7d9bff]!">
                {m.mk_loop_modes_title()}
              </Overline>
            </div>
            {modes.map(({ icon, title, desc }) => (
              <DarkCard key={title} icon={icon} title={title}>
                {desc}
              </DarkCard>
            ))}
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <DarkCard icon={ShieldCheck} title={m.mk_loop_verif_title()}>
              <ul className="flex flex-col gap-2">
                {[
                  m.mk_loop_verif_1(),
                  m.mk_loop_verif_2(),
                  m.mk_loop_verif_3(),
                ].map((v) => (
                  <li key={v} className="flex items-start gap-2">
                    <Check
                      className="mt-1 size-3.5 shrink-0 text-[#34d399]"
                      aria-hidden
                    />
                    <span>{v}</span>
                  </li>
                ))}
              </ul>
            </DarkCard>
            <DarkCard icon={Bot} title={m.mk_loop_guard_title()}>
              {m.mk_loop_guard_desc()}
            </DarkCard>
            <DarkCard icon={Waves} title={m.mk_loop_wave_title()}>
              {m.mk_loop_wave_desc()}
            </DarkCard>
          </div>
        </details>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- 6. control tower (cost · drift · readiness · benchmark)

export function ControlSection() {
  return (
    <section id="control" className="mk-section">
      <Container>
        <SectionHead
          align="left"
          overline={m.mk_ctrl_overline()}
          title={m.mk_ctrl_title()}
          lead={m.mk_ctrl_sub()}
        />
        <div className="mt-10 grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="mk-card overflow-hidden p-6">
            <h3>{m.mk_ctrl_big1_title()}</h3>
            <p className="mk-muted mt-3 leading-relaxed">
              {m.mk_ctrl_big1_desc()}
            </p>
            <img
              src="/landing/cost.png"
              alt={m.mk_ctrl_big1_alt()}
              width={1440}
              height={900}
              loading="lazy"
              decoding="async"
              className="mk-shot mt-6"
            />
          </div>
          <div className="flex flex-col gap-7">
            <FeatureCol title={m.mk_ctrl_big2_title()}>
              {m.mk_ctrl_big2_desc()}
            </FeatureCol>
            <div className="border-t border-[var(--mk-border)] pt-6">
              <FeatureCol title={m.mk_ctrl_s1_title()}>
                {m.mk_ctrl_s1_desc()}
              </FeatureCol>
              <MkLink
                to="/readiness"
                variant="secondary"
                arrow
                className="mt-4"
              >
                {m.mk_ctrl_s1_link()}
              </MkLink>
            </div>
            <div className="border-t border-[var(--mk-border)] pt-6">
              <FeatureCol title={m.mk_ctrl_s2_title()}>
                {m.mk_ctrl_s2_desc()}
              </FeatureCol>
              <MkLink
                to="/benchmark"
                variant="secondary"
                arrow
                className="mt-4"
              >
                {m.mk_ctrl_s2_link()}
              </MkLink>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- 10. Bystep Studio (agencies) — warm frame + four points

export function StudioSection() {
  const points: Array<{ icon: LucideIcon; title: string; desc: string }> = [
    {
      icon: Calculator,
      title: m.mk_studio_1_title(),
      desc: m.mk_studio_1_desc(),
    },
    {
      icon: FileSignature,
      title: m.mk_studio_2_title(),
      desc: m.mk_studio_2_desc(),
    },
    { icon: Users, title: m.mk_studio_3_title(), desc: m.mk_studio_3_desc() },
    {
      icon: FolderArchive,
      title: m.mk_studio_4_title(),
      desc: m.mk_studio_4_desc(),
    },
  ]
  return (
    <section
      id="studio"
      className="scroll-mt-20 bg-[var(--mk-bg-muted)] py-24 lg:py-32"
    >
      <Container>
        <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="flex flex-col items-start gap-5">
            <Overline>{m.mk_studio_overline()}</Overline>
            <h2 className="max-w-[16ch]">{m.mk_studio_title()}</h2>
            <p className="mk-lead max-w-[52ch]">{m.mk_studio_sub()}</p>
            <ul className="mt-4 grid gap-5 sm:grid-cols-2">
              {points.map(({ icon: I, title, desc }) => (
                <li key={title} className="flex flex-col gap-2">
                  <I className="size-5 text-[var(--mk-fg)]" aria-hidden />
                  <h3 className="text-base!">{title}</h3>
                  <p className="mk-muted text-sm leading-relaxed">{desc}</p>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-sm font-medium text-[var(--mk-fg-muted)]">
              {m.mk_studio_meta()}
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              <MkLink to="/studio">{m.mk_studio_cta_primary()}</MkLink>
              <MkLink
                to="/pricing"
                search={{ voucher: undefined }}
                variant="secondary"
                arrow
              >
                {m.mk_studio_cta_secondary()}
              </MkLink>
            </div>
          </div>
          {/* the client page centres an ~800px column, so zoom into it instead of showing 1440px of margin */}
          <Frame
            src="/landing/client.png"
            alt={m.mk_studio_frame_alt()}
            width={1440}
            height={900}
            tone="warm"
            offset="sm"
            className="max-h-[560px] lg:sticky lg:top-24"
            imgClassName="w-[150%]! max-w-none -ml-[25%]"
          />
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- 14. compact pricing (6 tiers → /pricing)

export function PricingSection() {
  const { data, isPending, isError, refetch } = usePricing()
  const [currency, setCurrency] = useState<'IDR' | 'USD'>(
    getLocale() === 'id' ? 'IDR' : 'USD',
  )
  const tiers = (data?.plans ?? [])
    .filter(
      (plan) =>
        plan.isActive &&
        (plan.price === 0 ||
          (plan.currency === currency &&
            (plan.durationDays === 30 ||
              plan.slug.replace(/-usd$/, '') === 'campus'))),
    )
    .filter(
      (plan, index, all) =>
        all.findIndex(
          (other) =>
            other.slug.replace(/-usd$/, '') === plan.slug.replace(/-usd$/, ''),
        ) === index,
    )
    .sort((a, b) => a.sortOrder - b.sortOrder)
  const format = new Intl.NumberFormat(
    getLocale() === 'id' ? 'id-ID' : 'en-US',
    {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'IDR' ? 0 : 2,
      minimumFractionDigits: 0,
    },
  )
  return (
    <section id="pricing" className="scroll-mt-20 py-24 lg:py-32">
      <Container>
        <SectionHead
          overline={m.mk_price_overline()}
          title={m.mk_price_title()}
          lead={m.mk_price_sub()}
        />
        <div className="mt-8 flex justify-center">
          <div
            className="mk-language"
            role="group"
            aria-label={m.mk_price_currency()}
          >
            {(['IDR', 'USD'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={currency === value}
                onClick={() => setCurrency(value)}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
        {isPending ? (
          <p role="status" className="mk-muted py-12 text-center">
            {m.mk_price_loading()}
          </p>
        ) : isError || !tiers.length ? (
          <div
            role="status"
            className="mk-muted flex flex-col items-center gap-4 py-12"
          >
            <p>{m.mk_price_error()}</p>
            <button
              type="button"
              className="mk-btn mk-btn-secondary"
              onClick={() => {
                void refetch()
              }}
            >
              {m.mk_price_retry()}
            </button>
          </div>
        ) : (
          <ul className="mk-pricing-grid mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {tiers.map((plan) => {
              const featured = plan.slug.replace(/-usd$/, '') === 'pro'
              return (
                <li
                  key={plan.id}
                  data-plan={plan.slug}
                  className={cn(
                    'flex flex-col gap-3 rounded-[var(--mk-radius)] border p-6',
                    featured
                      ? 'mk-dark-section border-transparent'
                      : 'mk-card-white',
                  )}
                >
                  <h3
                    className={cn(
                      'text-sm! font-medium! tracking-[0.02em] uppercase',
                      featured ? 'text-[#7d9bff]!' : 'text-[var(--mk-accent)]!',
                    )}
                  >
                    {plan.name}
                  </h3>
                  <p
                    data-price={plan.price}
                    className="font-heading text-3xl font-semibold tracking-[-0.03em]"
                  >
                    {format.format(plan.price)}
                  </p>
                  <p className="mk-muted text-sm">
                    {plan.price === 0
                      ? m.mk_hero_fact_4()
                      : plan.durationDays === 30
                        ? m.mk_price_per_month()
                        : m.mk_price_days({ days: String(plan.durationDays) })}
                  </p>
                  <p className="mk-muted text-sm leading-relaxed">
                    {m.mk_price_limits({
                      prd: String(plan.limits.prdPerMonth),
                      chat: String(plan.limits.chatMessagesPerMonth),
                      parallel: parallelLabel(plan),
                    })}
                  </p>
                  <MkLink
                    to="/pricing"
                    variant="secondary"
                    arrow
                    className="mt-auto self-start"
                  >
                    {m.mk_price_choose({ name: plan.name })}
                  </MkLink>
                </li>
              )
            })}
          </ul>
        )}
        <div className="mt-8 flex flex-col items-center gap-4 text-center">
          <p className="mk-muted max-w-3xl text-sm leading-relaxed">
            {m.mk_price_more()}
          </p>
          <MkLink to="/pricing" variant="secondary" arrow>
            {m.mk_price_cta()}
          </MkLink>
        </div>
      </Container>
    </section>
  )
}

/**
 * Agents at once for a plan card: the explicit `pricing_plan.limits.parallelRuns` when the catalog carries it
 * (Orkestrator E6), otherwise `PARALLEL_DEFAULTS` for the base slug — the same resolution the runner enforces.
 */
function parallelLabel(plan: { slug: string; limits: object }) {
  const explicit = (plan.limits as { parallelRuns?: unknown }).parallelRuns
  const n = resolveParallelLimits(
    { parallelRuns: typeof explicit === 'number' ? explicit : undefined },
    plan.slug,
    {},
  ).parallelRuns
  return n === UNLIMITED ? '∞' : String(n)
}

// ---------------------------------------------------------------- 15. compare strip (internal links to /compare/*, /integrations, /templates)

/** Proper nouns of the tools we publish comparisons for — slugs are routes under /compare. Not translated. */
const COMPARE: Array<{ slug: string; name: string }> = [
  { slug: 'spec-kit', name: 'Spec Kit' },
  { slug: 'kiro', name: 'Kiro' },
  { slug: 'traycer', name: 'Traycer' },
  { slug: 'openspec', name: 'OpenSpec' },
  { slug: 'backlog-md', name: 'Backlog.md' },
  { slug: 'ngodingpakeai', name: 'ngodingpakeai' },
  { slug: 'miro', name: 'Miro' },
  { slug: 'conductor', name: 'Conductor' },
  { slug: 'superset', name: 'Superset' },
  { slug: 'ao', name: 'AO' },
]

const chip =
  'inline-flex items-center gap-1.5 rounded-full border border-[var(--mk-border)] bg-white px-4 py-2 text-[15px] font-medium text-[var(--mk-fg)] transition-colors hover:border-[var(--mk-fg)]'

export function CompareStrip() {
  return (
    <section className="bg-[var(--mk-bg-muted)] py-16">
      <Container className="flex flex-col items-center gap-6 text-center">
        <h2 className="text-2xl! sm:text-3xl!">{m.mk_cmp_title()}</h2>
        <p className="mk-muted max-w-[56ch]">{m.mk_cmp_sub()}</p>
        <ul className="flex flex-wrap justify-center gap-2.5">
          {COMPARE.map(({ slug, name }) => (
            <li key={slug}>
              <Link to="/compare/$slug" params={{ slug }} className={chip}>
                <Layers
                  className="size-3.5 text-[var(--mk-fg-muted)]"
                  aria-hidden
                />
                {name}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/integrations" className={chip}>
              <Package
                className="size-3.5 text-[var(--mk-fg-muted)]"
                aria-hidden
              />
              {m.mk_cmp_integrations()}
            </Link>
          </li>
          <li>
            <Link to="/templates" className={chip}>
              <BadgeCheck
                className="size-3.5 text-[var(--mk-fg-muted)]"
                aria-hidden
              />
              {m.mk_cmp_templates()}
            </Link>
          </li>
        </ul>
      </Container>
    </section>
  )
}
